import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, LinearProgress, Paper, Chip, Grid, CircularProgress,
  List, ListItem, ListItemButton, ListItemText, Divider, Collapse, IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InboxIcon from '@mui/icons-material/Inbox';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import WmsBarcodeScanner from '../../shared/WmsBarcodeScanner';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import { waveService } from '../../services/waveService';
import { equipmentService } from '../../services/equipmentService';
import { PickListItem } from './PickingActiveFlow';
import { matchesScannedValue } from '../../shared/scanMatching';

export interface SlotAssignment {
  slotId: string;
  slotCode: string;
  barcodeData?: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  assignedItems: number;
  totalItems: number;
  isComplete: boolean;
}

interface PackingSlot {
  id: string;
  slotCode: string;
  barcodeData?: string;
  status: string;
  currentOrderId?: string;
  currentOrderNumber?: string;
  isActive: boolean;
}

interface PickingSortingFlowProps {
  waveId: string;
  waveNumber: string;
  cartBarcode: string;
  warehouseId: string;
  pickList: PickListItem[];
  onComplete: (sortedCount: number, slotCount: number, assignments: SlotAssignment[]) => void;
}

type SortingStep = 'scan-product' | 'scan-shelf';

const PickingSortingFlow: React.FC<PickingSortingFlowProps> = ({
  waveId, waveNumber, cartBarcode, warehouseId, pickList, onComplete,
}) => {
  const { t } = useTranslation('warehouse');
  const [step, setStep] = useState<SortingStep>('scan-product');
  const [currentProduct, setCurrentProduct] = useState<PickListItem | null>(null);
  const [targetSlotId, setTargetSlotId] = useState<string | null>(null);
  const [targetSlotCode, setTargetSlotCode] = useState('');
  const [isExistingSlot, setIsExistingSlot] = useState(false);
  const [slotAssignments, setSlotAssignments] = useState<Map<string, SlotAssignment>>(new Map());
  const [sortedItemIds, setSortedItemIds] = useState<Set<string>>(new Set());
  const [availableSlots, setAvailableSlots] = useState<PackingSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [saving, setSaving] = useState(false);
  // Sipariş listesinde açılan (dropdown) siparişlerin id kümesi.
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const { notification, notify, clear } = useWmsNotification();

  const toggleOrderExpanded = useCallback((orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  // Split pick satırları aynı orderItemId ile gelebilir; ayrıştırmada kalem tekilleştirilir.
  const sortableItems = useMemo(() => {
    const seen = new Set<string>();
    const unique: PickListItem[] = [];
    for (const i of pickList) {
      if (i.status !== 'Picked' && i.status !== 'Sorted') continue;
      if (seen.has(i.orderItemId)) continue;
      seen.add(i.orderItemId);
      unique.push(i);
    }
    return unique;
  }, [pickList]);
  const totalToSort = sortableItems.length;
  const sortedCount = sortedItemIds.size;
  const progress = totalToSort > 0 ? (sortedCount / totalToSort) * 100 : 0;

  // Toplayanın elindeki (bu dalgadaki) siparişlerin listesi: barkod/sipariş no ile birlikte
  // her siparişin kaç ürünü ayrıştırıldı bilgisini gösterir.
  const orderList = useMemo(() => {
    const map = new Map<string, {
      orderId: string; orderNumber: string; customerName: string;
      total: number; sorted: number;
      items: { orderItemId: string; productName: string; barcode: string; quantity: number; sorted: boolean }[];
    }>();
    for (const it of sortableItems) {
      const entry = map.get(it.orderId) || {
        orderId: it.orderId,
        orderNumber: it.orderNumber || it.orderId.slice(0, 8),
        customerName: it.customerName || '',
        total: 0,
        sorted: 0,
        items: [],
      };
      const isSorted = sortedItemIds.has(it.orderItemId);
      entry.total += 1;
      if (isSorted) entry.sorted += 1;
      entry.items.push({
        orderItemId: it.orderItemId,
        productName: it.productName || t('screens.picking.common.productFallback'),
        barcode: it.barcode || it.sku || '-',
        quantity: it.quantity,
        sorted: isSorted,
      });
      map.set(it.orderId, entry);
    }
    return Array.from(map.values());
  }, [sortableItems, sortedItemIds, t]);

  useEffect(() => {
    // Ekrandan cikis-donus senaryosunda daha once ayrismis urunleri
    // tekrar okutma zorunlulugu olmasin.
    const initiallySorted = new Set(
      pickList
        .filter((i) => i.status === 'Sorted')
        .map((i) => i.orderItemId)
    );
    setSortedItemIds(initiallySorted);
  }, [pickList]);

  useEffect(() => {
    (async () => {
      setLoadingSlots(true);
      try {
        const res = await equipmentService.getShelves(warehouseId);
        const shelves: any[] = res.data?.data || res.data || [];
        const allSlots: PackingSlot[] = [];
        for (const shelf of shelves) {
          if (!shelf.isActive) continue;
          const detail = await equipmentService.getShelfDetail(shelf.id);
          const slots: any[] = detail.data?.data?.slots || [];
          for (const s of slots) {
            if (s.isActive && !s.isDeleted) {
              allSlots.push({
                id: s.id,
                slotCode: s.slotCode,
                barcodeData: s.barcodeData,
                status: s.status || 'Empty',
                currentOrderId: s.currentOrderId,
                currentOrderNumber: s.currentOrderNumber,
                isActive: s.isActive,
              });
            }
          }
        }
        setAvailableSlots(allSlots);
      } catch { setAvailableSlots([]); }
      setLoadingSlots(false);
    })();
  }, [warehouseId]);

  const handleProductScan = useCallback((barcode: string) => {
    const item = sortableItems.find(p =>
      (
        matchesScannedValue(barcode, p.barcode) ||
        matchesScannedValue(barcode, p.sku) ||
        matchesScannedValue(barcode, p.productId)
      )
      && !sortedItemIds.has(p.orderItemId)
    );
    if (!item) {
      notify({ type: 'error', message: t('screens.picking.sortingFlow.notFoundOrSorted'), duration: 3000 });
      return;
    }

    const orderId = item.orderId;
    const existing = Array.from(slotAssignments.values()).find(a => a.orderId === orderId);
    const existingFromServer = availableSlots.find(
      (s) => s.currentOrderId === orderId,
    );

    if (existing) {
      setTargetSlotId(existing.slotId);
      setTargetSlotCode(existing.slotCode);
      setIsExistingSlot(true);
    } else if (existingFromServer) {
      setTargetSlotId(existingFromServer.id);
      setTargetSlotCode(existingFromServer.slotCode);
      setIsExistingSlot(true);
    } else {
      const usedSlotIds = new Set(Array.from(slotAssignments.values()).map(a => a.slotId));
      const emptySlot = availableSlots.find(s =>
        (String(s.status || '').toLowerCase() === 'empty' && !s.currentOrderId) && !usedSlotIds.has(s.id)
      );
      if (!emptySlot) {
        notify({ type: 'error', message: t('screens.picking.sortingFlow.noEmptySlot'), duration: 4000 });
        return;
      }
      setTargetSlotId(emptySlot.id);
      setTargetSlotCode(emptySlot.slotCode);
      setIsExistingSlot(false);
    }

    setCurrentProduct(item);
    setStep('scan-shelf');
    notify({ type: 'info', message: t('screens.picking.sortingFlow.scannedToast', { name: item.productName || t('screens.picking.common.productFallback') }), duration: 1500 });
  }, [sortableItems, sortedItemIds, slotAssignments, availableSlots, notify, t]);

  const handleShelfScan = useCallback(async (barcode: string) => {
    if (!currentProduct || !targetSlotId || saving) return;

    const targetSlot = availableSlots.find((s) => s.id === targetSlotId);
    const isTargetMatch = !!targetSlot && (
      matchesScannedValue(barcode, targetSlot.barcodeData) ||
      matchesScannedValue(barcode, targetSlot.slotCode) ||
      matchesScannedValue(barcode, targetSlot.id) ||
      matchesScannedValue(barcode, targetSlotCode)
    );

    if (!isTargetMatch) {
      notify({ type: 'error', message: t('screens.picking.sortingFlow.wrongSlot', { code: targetSlotCode }), duration: 4000 });
      return;
    }

    setSaving(true);
    try {
      await waveService.sortItem(waveId, {
        OrderItemId: currentProduct.orderItemId,
        SlotId: targetSlotId,
      });

      const orderId = currentProduct.orderId;
      const newSorted = new Set(sortedItemIds);
      newSorted.add(currentProduct.orderItemId);
      setSortedItemIds(newSorted);

      const newAssignments = new Map(slotAssignments);
      const existing = newAssignments.get(targetSlotId);
      if (existing) {
        existing.assignedItems += 1;
        existing.isComplete = existing.assignedItems >= existing.totalItems;
      } else {
        const orderItems = sortableItems.filter(i => i.orderId === orderId);
        newAssignments.set(targetSlotId, {
          slotId: targetSlotId,
          slotCode: targetSlotCode,
          orderId,
          orderNumber: currentProduct.orderNumber || orderId.substring(0, 8),
          customerName: currentProduct.customerName || '',
          assignedItems: 1,
          totalItems: orderItems.length,
          isComplete: orderItems.length <= 1,
        });
      }
      setSlotAssignments(newAssignments);

      notify({ type: 'success', message: t('screens.picking.sortingFlow.sortedToast', { done: newSorted.size, total: totalToSort }), duration: 2000 });

      if (newSorted.size >= totalToSort) {
        onComplete(newSorted.size, newAssignments.size, Array.from(newAssignments.values()));
      } else {
        setCurrentProduct(null);
        setTargetSlotId(null);
        setTargetSlotCode('');
        setStep('scan-product');
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.sortingFlow.sortSaveFailed') });
    }
    setSaving(false);
  }, [currentProduct, targetSlotId, targetSlotCode, waveId, sortedItemIds, slotAssignments, sortableItems, totalToSort, onComplete, saving, notify, availableSlots, t]);

  if (loadingSlots) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  const assignmentsList = Array.from(slotAssignments.values());

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <WmsNotification notification={notification} onClose={clear} />

      {/* Header */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.main', color: 'common.white' }}>
        <Chip
          label={t('screens.picking.sortingFlow.header')}
          color="secondary"
          size="small"
          sx={{ fontWeight: 700, '& .MuiChip-label': { color: 'common.white' } }}
        />
        <LinearProgress variant="determinate" value={progress}
          color="secondary" sx={{ flex: 1, height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.35)' }} />
        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', color: 'common.white' }}>
          {t('screens.picking.common.itemsCount', { count: `${sortedCount}/${totalToSort}` })}
        </Typography>
        {cartBarcode && (
          <Chip
            size="small"
            label={t('screens.picking.common.cartLabel', { code: cartBarcode })}
            variant="outlined"
            sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.7)' }}
          />
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {step === 'scan-product' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              {t('screens.picking.sortingFlow.scanProductTitle')}
            </Typography>
            <WmsBarcodeScanner
              onScan={handleProductScan}
              placeholder={t('screens.picking.common.scanProductPlaceholder')}
              size="medium"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('screens.picking.sortingFlow.remaining', { count: totalToSort - sortedCount })}
            </Typography>
          </Paper>
        )}

        {step === 'scan-shelf' && currentProduct && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">{t('screens.picking.sortingFlow.productLabel')}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {currentProduct.productName || t('screens.picking.common.productFallback')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('screens.picking.common.barcodeValue', { value: currentProduct.barcode || currentProduct.sku || '-' })}
            </Typography>

            {isExistingSlot ? (
              <Box sx={{ bgcolor: 'info.dark', color: '#fff', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {t('screens.picking.sortingFlow.sameSlot', { code: targetSlotCode })}
                </Typography>
                <Typography variant="body2">
                  {t('screens.picking.sortingFlow.sameSlotHint')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: 'warning.dark', color: '#fff', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {t('screens.picking.sortingFlow.newSlot', { code: targetSlotCode })}
                </Typography>
                <Typography variant="body2">
                  {t('screens.picking.sortingFlow.newSlotHint')}
                </Typography>
              </Box>
            )}

            <WmsBarcodeScanner
              onScan={handleShelfScan}
              placeholder={t('screens.picking.sortingFlow.scanSlotPlaceholder')}
              size="medium"
              disabled={saving}
            />
          </Paper>
        )}

        {/* Sipariş Listesi (barkod/sipariş no ile) */}
        {orderList.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ReceiptLongIcon fontSize="small" color="primary" />
              <Typography variant="overline" color="text.secondary">
                {t('screens.picking.sortingFlow.orderListTitle')}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={t('screens.picking.sortingFlow.orderListCount', { count: orderList.length })}
                sx={{ ml: 'auto', height: 20 }}
              />
            </Box>
            <Divider sx={{ mb: 0.5 }} />
            <List dense sx={{ maxHeight: 260, overflow: 'auto', py: 0 }}>
              {orderList.map((o, idx) => {
                const isCurrent = !!currentProduct && currentProduct.orderId === o.orderId;
                const isComplete = o.sorted >= o.total;
                const isExpanded = expandedOrders.has(o.orderId);
                return (
                  <React.Fragment key={o.orderId}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItemButton
                      onClick={() => toggleOrderExpanded(o.orderId)}
                      sx={{
                        px: 1, py: 0.5, borderRadius: 1,
                        bgcolor: isCurrent ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      }}
                    >
                      <QrCode2Icon
                        fontSize="small"
                        sx={{ mr: 1, color: isCurrent ? 'primary.main' : 'text.disabled' }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: isCurrent ? 700 : 600 }}>
                            {o.orderNumber}
                          </Typography>
                        }
                        secondary={o.customerName || undefined}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                      <Chip
                        size="small"
                        color={isComplete ? 'success' : 'default'}
                        variant={isComplete ? 'filled' : 'outlined'}
                        icon={isComplete ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
                        label={t('screens.picking.sortingFlow.slotItems', { assigned: o.sorted, total: o.total })}
                        sx={{ height: 22, mr: 0.5 }}
                      />
                      <IconButton size="small" edge="end" sx={{ p: 0.25 }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </ListItemButton>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List dense disablePadding sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                        {o.items.map(it => (
                          <ListItem key={it.orderItemId} sx={{ pl: 4.5, pr: 1, py: 0.25 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <QrCode2Icon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                    {it.barcode}
                                  </Typography>
                                  {it.quantity > 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                      x{it.quantity}
                                    </Typography>
                                  )}
                                  {it.sorted && <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />}
                                </Box>
                              }
                              secondary={it.productName}
                              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {/* Slot Visual Panel */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('screens.picking.sortingFlow.slotsStatus')}
          </Typography>
          <Grid container spacing={1}>
            {availableSlots.slice(0, 18).map(slot => {
              const assignment = assignmentsList.find(a => a.slotId === slot.id);
              const isSystemOccupied = !assignment && (
                !!slot.currentOrderId ||
                String(slot.status || '').toLowerCase() !== 'empty'
              );
              let bgColor = '#f8fafc';
              let borderColor = '#e2e8f0';
              let statusIcon = null;

              if (assignment) {
                if (assignment.isComplete) {
                  bgColor = '#e8f5e9';
                  borderColor = '#66bb6a';
                  statusIcon = <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
                } else {
                  bgColor = '#fff3e0';
                  borderColor = '#fb8c00';
                }
              } else if (isSystemOccupied) {
                bgColor = '#fff8e1';
                borderColor = '#ffb300';
              }

              const isTarget = slot.id === targetSlotId;

              return (
                <Grid item xs={4} sm={3} md={2} key={slot.id}>
                  <Box sx={{
                    p: 1, borderRadius: 1, bgcolor: bgColor,
                    border: isTarget ? '2px solid #90caf9' : `1px solid ${borderColor}`,
                    minHeight: 60, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    animation: isTarget ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.6 },
                    },
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
                        {slot.slotCode}
                      </Typography>
                      {statusIcon}
                    </Box>
                    {assignment ? (
                      <>
                        <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.2 }}>
                          {assignment.orderNumber?.substring(0, 12) || t('screens.picking.sortingFlow.orderFallback')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600 }}>
                          {t('screens.picking.sortingFlow.slotItems', { assigned: assignment.assignedItems, total: assignment.totalItems })}
                        </Typography>
                      </>
                    ) : isSystemOccupied ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <InboxIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                        <Typography variant="caption" sx={{ fontSize: 10, color: 'warning.dark', fontWeight: 600 }}>
                          {t('screens.picking.sortingFlow.occupied')} {slot.currentOrderNumber ? `(${slot.currentOrderNumber})` : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <InboxIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>{t('screens.picking.sortingFlow.empty')}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default PickingSortingFlow;
